import { DONT_SHOW_OPEN_SEATS_WARNING_AGAIN } from 'constants/user';
import { getIsDemoManagerPresetActive } from 'redux/modules/tic/tic.demo.selectors';
import {
  getDemoActivities,
  getDemoActivityMaybeHasMore,
  getDemoActivityXhrStatus,
  getDemoIsLoadingUsers,
  getDemoTotalUserCount,
} from 'redux/modules/users-page/demo/usersPage.demo.selectors';
import { getAccountFeatures, getHasReachedUserLimit } from 'redux/selectors/accounts';
import {
  getActivityMaybeHasMore,
  getActivityXhrStatus,
  getAllUserActivities,
} from 'redux/selectors/activities';
import { getIsAppInitialized } from 'redux/selectors/global';
import { getHasReachedTeamsLimit } from 'redux/selectors/teams';
import { getUserPermissions, getUserProperties } from 'redux/selectors/user';
import { getIsLoadingUsers } from 'redux/selectors/users';
import type { IState } from 'redux/state';
import { createSelector } from 'reselect';

const readShowUnoccupiedSeatsWarningModal = (state: IState) =>
  state.usersPage.showUnoccupiedSeatsWarningModal;

const readNumberOfUsers = (state: IState) => state.users.entries.length;

export const getUsersPageUi = createSelector(
  [
    getAccountFeatures,
    getUserPermissions,
    getIsAppInitialized,
    getHasReachedUserLimit,
    getHasReachedTeamsLimit,
    readShowUnoccupiedSeatsWarningModal,
    readNumberOfUsers,
    getDemoTotalUserCount,
    getIsDemoManagerPresetActive,
  ],
  (
    features,
    userPermissions,
    isAppInitialized,
    hasReachedUserLimit,
    hasReachedTeamsLimit,
    showUnoccupiedSeatsWarningModal,
    numberOfUsers,
    demoUserCount,
    isDemoManagerPresetActive
  ) => {
    return {
      isDashboardFeatureLocked: !features.people_dashboard,
      newUserButton: {
        isVisible: userPermissions.canCreate.users,
        isDisabled: !isAppInitialized || isDemoManagerPresetActive,
        reachedUserLimit: hasReachedUserLimit,
        // Demo mode never applies a change to the real workspace, so inviting
        // (New user or Import) is blocked while it's on, distinct from the
        // other isDisabled causes so the UI can explain why.
        isBlockedByDemoMode: isDemoManagerPresetActive,
      },
      manageTeamsButton: {
        isVisible: userPermissions.canRead.teams,
        isDisabled: !isAppInitialized,
        isLockedFeature: !features.teams,
      },
      newTeamButton: {
        isVisible: userPermissions.canCreate.teams,
        isDisabled: !isAppInitialized,
        isLockedFeature: hasReachedTeamsLimit,
      },
      showUnoccupiedSeatsWarningModal,
      numberOfUsers: demoUserCount ?? numberOfUsers,
    };
  }
);

export const getIsDontShowAgainChecked = createSelector(
  [getUserProperties],
  (userProperties): boolean => {
    const prop = userProperties[DONT_SHOW_OPEN_SEATS_WARNING_AGAIN];
    return (typeof prop === 'boolean' && prop) || false;
  }
);

/**
 * Whether the People header should wait before showing its user count. The demo
 * directory is present from the first render, so it never waits.
 */
export const getIsLoadingUsersForPeoplePage = createSelector(
  [getDemoIsLoadingUsers, getIsLoadingUsers],
  (demo, real) => demo ?? real
);

/**
 * The People page's Activities tab.
 *
 * The workspace activity feed is shared with the Projects dashboard and Project
 * Details, so the demo swap is scoped to this page rather than applied to the
 * shared selectors: elsewhere the feed must keep paginating and keep reporting
 * its real fetch state.
 */
export const getUsersPageActivities = createSelector(
  [getDemoActivities, getAllUserActivities],
  (demo, real) => demo ?? real
);

export const getUsersPageActivityXhrStatus = createSelector(
  [getDemoActivityXhrStatus, getActivityXhrStatus],
  (demo, real) => demo ?? real
);

export const getUsersPageActivityMaybeHasMore = createSelector(
  [getDemoActivityMaybeHasMore, getActivityMaybeHasMore],
  (demo, real) => demo ?? real
);
